/**
 * Email (decision 0064, `docs/design/wave-m-email-spec.md` §3): the shapes of invite-by-email, resend, the per-invite
 * email status, email availability and the test email, plus the personal-note rule. The web shows the note rule
 * inline and the cloud enforces the same function. No I/O.
 */
import { Schema } from "effect"
import { WorkspaceRole } from "./http"
import { Millis, NonNegInt } from "./ids"

/** Longest personal note on an invite email, in characters (code points) after cleanInviteNote. */
export const INVITE_NOTE_MAX_LENGTH = 280

/** A client-made id per click; the server uses it as the idempotency key of the email it sends. */
export const EmailRequestId = Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{8,64}$/))
export type EmailRequestId = typeof EmailRequestId.Type

/** What the inviter may give: one address. Same shape check as the web's parseEmails (members-page.tsx:34). */
export const InviteEmailAddress = Schema.String.check(
  Schema.isMaxLength(254),
  Schema.isPattern(/^[^\s@<>()",;:]+@[^\s@<>()",;:]+\.[^\s@<>()",;:]+$/),
)

/** Invite emails offer member and admin; owners are made later from Members (as today's sheet). */
export const InviteEmailRole = Schema.Literals(["member", "admin"])
export type InviteEmailRole = typeof InviteEmailRole.Type

/** POST /api/workspaces/:ws/invitations/email */
export const InviteByEmailBody = Schema.Struct({
  /** The server lowercases and trims. */
  email: InviteEmailAddress,
  role: InviteEmailRole,
  /** Raw note; the server applies cleanInviteNote + inviteNoteProblem. Capped here only against huge bodies. */
  note: Schema.optional(Schema.NullOr(Schema.String.check(Schema.isMaxLength(2000)))),
  /** The project named in the email; with decision 0129 the invitee also joins it. */
  projectId: Schema.optional(Schema.NullOr(Schema.String.check(Schema.isMaxLength(128)))),
  /**
   * Decision 0129: the projects the invitee joins when they accept (as `member`), besides `projectId`. None: they join
   * no project and ask to be added (workspace-visible projects they can still see and join).
   */
  projectIds: Schema.optional(
    Schema.Array(Schema.String.check(Schema.isMaxLength(128))).check(Schema.isMaxLength(20)),
  ),
  requestId: EmailRequestId,
})
export type InviteByEmailBody = typeof InviteByEmailBody.Type

/** Decision 0129: `PUT /api/workspaces/:ws/invitations/:invitationId/projects` (the projects joined on accept). */
export const InvitationProjectsBody = Schema.Struct({
  projectIds: Schema.Array(Schema.String.check(Schema.isMaxLength(128))).check(Schema.isMaxLength(20)),
})
export type InvitationProjectsBody = typeof InvitationProjectsBody.Type

/** POST /api/workspaces/:ws/invitations/:invitationId/resend */
export const ResendInviteBody = Schema.Struct({ requestId: EmailRequestId })
export type ResendInviteBody = typeof ResendInviteBody.Type

/** Why an email didn't go out, in terms the web turns into one plain sentence (web/features/settings/email-words.ts). */
export const EmailProblem = Schema.Literals([
  // from Cloudflare or our pipeline
  /** No binding, sender or domain not set up. */
  "not_configured",
  /** E_RECIPIENT_SUPPRESSED, or skipped because it was suppressed in the last 30 days. */
  "suppressed",
  /** E_DAILY_LIMIT_EXCEEDED, or one of Minga's Minga-wide daily ceilings (per kind or all kinds). */
  "daily_limit",
  /** E_RATE_LIMIT_EXCEEDED after the one retry. */
  "busy",
  /** The message was refused (validation, headers, size, render, a foreign link): our bug. */
  "invalid",
  /** Delivery or Cloudflare failed, or an unknown error after the retry. */
  "failed",
  // Minga's own limits and rules (the email was not attempted)
  /** Per inviter (hour or day, stricter for young accounts) or per IP. */
  "inviter_limit",
  "workspace_limit",
  /** Per recipient in this workspace, or across all workspaces. */
  "recipient_limit",
  "resend_limit",
  "declined",
  /** Wave E (decision 0063, spec §8): the inviter's own address isn't verified, so Minga emails no invite from them. */
  "unverified",
])
export type EmailProblem = typeof EmailProblem.Type

/** The email part of an invite or resend answer. `sending`: the same requestId is still in flight. */
export const InviteEmailOutcome = Schema.Union([
  Schema.Struct({ status: Schema.Literals(["sent", "sending"]), at: Millis }),
  Schema.Struct({ status: Schema.Literals(["failed", "skipped"]), problem: EmailProblem, at: Millis }),
])
export type InviteEmailOutcome = typeof InviteEmailOutcome.Type

export const InviteByEmailResult = Schema.Struct({
  invitation: Schema.Struct({
    id: Schema.String,
    email: Schema.String,
    role: WorkspaceRole,
    expiresAt: Millis,
  }),
  /** `<app origin>/invite/<id>`: the copy-link fallback, always present. */
  link: Schema.String,
  email: InviteEmailOutcome,
})
export type InviteByEmailResult = typeof InviteByEmailResult.Type

/** GET /api/workspaces/:ws/invitations/email-status (owner/admin): the latest email per invitation. */
export const InviteEmailStatus = Schema.Struct({
  invitationId: Schema.String,
  status: Schema.Literals(["sending", "sent", "failed", "skipped"]),
  problem: Schema.NullOr(EmailProblem),
  at: Millis,
  /** Emails Cloudflare accepted for this invitation so far. */
  sentCount: NonNegInt,
  /** Whether a Resend now would be allowed by the per-invitation rules (the web also needs pending + unexpired). */
  canResend: Schema.Boolean,
  /** When Resend becomes possible; null when it is possible now or never again (sentCount ≥ 3). */
  resendAt: Schema.NullOr(Millis),
})
export type InviteEmailStatus = typeof InviteEmailStatus.Type
export const InviteEmailStatuses = Schema.Struct({
  enabled: Schema.Boolean,
  invitations: Schema.Array(InviteEmailStatus),
})
export type InviteEmailStatuses = typeof InviteEmailStatuses.Type

/** GET /api/me/email (session): whether this Minga can send email at all. */
export const EmailAvailability = Schema.Struct({ enabled: Schema.Boolean })
export type EmailAvailability = typeof EmailAvailability.Type

/** POST /api/me/test-email */
export const TestEmailBody = Schema.Struct({ requestId: EmailRequestId })
export type TestEmailBody = typeof TestEmailBody.Type
export const TestEmailResult = Schema.Struct({
  status: Schema.Literal("sent"),
  /** The signed-in person's own address, masked like maskEmail ("j…@every.to"). */
  to: Schema.String,
  at: Millis,
})
export type TestEmailResult = typeof TestEmailResult.Type

/**
 * POST /api/password-reset/check: whether an emailed reset link still works, before the page asks for a new password.
 * The token goes in the body, never a URL of ours.
 */
export const ResetTokenCheckBody = Schema.Struct({ token: Schema.String.check(Schema.isMaxLength(256)) })
export type ResetTokenCheckBody = typeof ResetTokenCheckBody.Type
export const ResetTokenCheckResult = Schema.Struct({ valid: Schema.Boolean })
export type ResetTokenCheckResult = typeof ResetTokenCheckResult.Type

/**
 * Error bodies of the email routes. `problem` only with `email_failed`; `resendAt` only with `resend_limit`
 * (null = never again).
 */
export const EmailRouteError = Schema.Literals([
  "invalid_body",
  "note_too_long",
  "note_has_link",
  "unknown_project",
  "forbidden",
  "not_found",
  "invitation_not_pending",
  "invitation_expired",
  "resend_limit",
  "rate_limited",
  "not_configured",
  "email_failed",
])
export type EmailRouteError = typeof EmailRouteError.Type

// ---- the personal note ----

/**
 * The dots a mail client accepts between the labels of a host name: `.` and U+3002, U+FF0E, U+FF61. A regex source
 * fragment, shared with @minga/emails' cleanName (copied there, since that package doesn't depend on the protocol).
 */
export const DOT = "[.\\u3002\\uFF0E\\uFF61]"

/**
 * A phone-like run: an optional `+` or `(`, then at least 7 digits with only spaces, dots, dashes, slashes or
 * parentheses between them. Gmail and iOS autolink phone numbers, so a note saying "call +1 888 555 0199" would make
 * callback phishing work from minga.run with valid DKIM. Not global: build a `g` copy from `.source` to find every run.
 * An ISO date on its own is excepted (`isExceptedPhoneRun`).
 */
export const PHONE_LIKE = /[+(]?\d(?:[\s.\-/()]*\d){6,}/u

/** A date like 2026-10-20: eight digits, so it would read as phone-like. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/u
/** An ISO date and the hour of a time right after it ("2026-10-20 14" of "2026-10-20 14:00"). */
const ISO_DATE_HOUR = /^\d{4}-\d{2}-\d{2}\s\d{1,2}$/u

/**
 * Whether a PHONE_LIKE run in `text` (at `index`) is an ordinary date, not a number to call: an ISO date on its own,
 * or an ISO date with the hour of a following `HH:MM` (the run stops at the colon, so it never holds a phone).
 */
export const isExceptedPhoneRun = (run: string, text: string, index: number): boolean =>
  ISO_DATE.test(run) || (ISO_DATE_HOUR.test(run) && text[index + run.length] === ":")

/** Whether `text` holds a phone-like run that isn't an excepted date. */
export const hasPhoneLike = (text: string): boolean => {
  for (const m of text.matchAll(new RegExp(PHONE_LIKE.source, "gu"))) {
    if (!isExceptedPhoneRun(m[0], text, m.index)) return true
  }
  return false
}

const LABEL = "[\\p{L}\\p{N}-]"
const NOT_LABEL_BEFORE = `(?<!${LABEL})`
const NOT_LABEL_AFTER = `(?!${LABEL})`

/** A scheme ("https://", "mailto://"). */
const SCHEME = /[a-z][a-z0-9+.-]*:\/\//iu
/** `www` and a dot, at the start of a word. */
const WWW = new RegExp(`${NOT_LABEL_BEFORE}www${DOT}`, "iu")
/** Anything a mail client reads as an address. */
const EMAIL_LIKE = /[^\s@]+@[^\s@]+/iu
/**
 * A host name: labels of letters, digits and dashes, any of DOT between them, a TLD of two or more letters. Lookarounds
 * instead of `\b`, which is ASCII-only in JS even with `u`; `\p{L}` so `пример.рф` and `evil` U+3002 `com` match.
 */
const DOMAIN_LIKE = new RegExp(
  `${NOT_LABEL_BEFORE}${LABEL}+(?:${DOT}${LABEL}+)*${DOT}\\p{L}{2,}${NOT_LABEL_AFTER}`,
  "iu",
)

// C0/C1 controls, the line and paragraph separators, and the bidi embeddings, overrides and isolates (which can make
// text read backwards). `\u` escapes only: a literal U+2028 inside a regex literal is a line break in the source.
// biome-ignore lint/suspicious/noControlCharactersInRegex: replacing control characters is the point
const CONTROL = /[\u0000-\u001F\u007F-\u009F\u2028\u2029\u202A-\u202E\u2066-\u2069]/gu

/**
 * Normalise a note: NFKC first (fullwidth letters, digits and dots become plain ones); CRLF → LF; C0/C1 controls,
 * U+2028/2029, bidi overrides (U+202A–202E, U+2066–2069) → space; runs of whitespace → one space; each line trimmed;
 * at most one empty line in a row; trimmed. Idempotent.
 */
export const cleanInviteNote = (note: string): string =>
  note
    .normalize("NFKC")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replace(CONTROL, " ").replace(/\s+/gu, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()

/** Length in code points (what a person counts; an emoji is one). */
export const codePointLength = (text: string): number => {
  let n = 0
  for (const _ of text) n++
  return n
}

/** Whether a mail client would turn some of `text` into a link: a web address, an email address or a phone number. */
export const hasContactLink = (text: string): boolean =>
  SCHEME.test(text) || WWW.test(text) || EMAIL_LIKE.test(text) || DOMAIN_LIKE.test(text) || hasPhoneLike(text)

/**
 * Why a note can't be sent, or null. Length is counted in code points after cleanInviteNote. "has_link" covers every
 * contact detail a mail client turns into a link: web addresses, email addresses and phone numbers (an ISO date is
 * fine). Mail clients autolink all of these, so escaping isn't enough.
 */
export const inviteNoteProblem = (note: string): "too_long" | "has_link" | null => {
  const clean = cleanInviteNote(note)
  if (codePointLength(clean) > INVITE_NOTE_MAX_LENGTH) return "too_long"
  if (hasContactLink(clean)) return "has_link"
  return null
}
